import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, MapPin, Calendar, Filter, Sun } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("hb_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Kon leads niet laden");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("hb_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("hb_admin_token");
    navigate("/admin/login");
  };

  const visible = leads.filter((l) => filter === "all" ? true : l.status === filter);

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full gradient-pink flex items-center justify-center">
              <Sun className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-black text-lg text-navy-900">Helios Admin</span>
          </div>
          <button onClick={logout} className="text-sm flex items-center gap-2 text-slate-600 hover:text-brand transition-colors" data-testid="admin-logout">
            <LogOut size={16} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-black text-navy-900">Leads</h1>
            <p className="text-slate-600 mt-1">{leads.length} totaal · {leads.filter((l) => l.status === "new").length} nieuw</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full p-1">
            <Filter className="text-slate-400 ml-2" size={16} />
            {["all", "new", "contacted", "won", "lost"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${filter === s ? "bg-brand text-white" : "text-navy-800 hover:bg-slate-50"}`}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "Alle" : s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500">Laden...</div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center text-slate-500">Geen leads gevonden.</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-3">
              {visible.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className={`w-full text-left bg-white border rounded-2xl p-5 hover:border-brand transition-all ${selected?.id === l.id ? "border-brand shadow-[0_8px_24px_rgba(255,47,125,0.12)]" : "border-slate-200"}`}
                  data-testid={`lead-row-${l.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-navy-900">{l.name}</div>
                      <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span className="flex items-center gap-1.5"><Mail size={13} /> {l.email}</span>
                        {l.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {l.phone}</span>}
                        {l.postcode && <span className="flex items-center gap-1.5"><MapPin size={13} /> {l.postcode}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        l.status === "new" ? "bg-brand-soft text-brand-deep" :
                        l.status === "contacted" ? "bg-blue-50 text-blue-700" :
                        l.status === "won" ? "bg-emerald-50 text-emerald-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>{l.status}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={11} /> {new Date(l.created_at).toLocaleDateString("nl-NL")}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <aside className="bg-white rounded-3xl border border-slate-200 p-6 lg:sticky lg:top-24 h-fit" data-testid="lead-detail-panel">
              {selected ? (
                <>
                  <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">Lead detail</div>
                  <h3 className="font-heading text-2xl font-black text-navy-900">{selected.name}</h3>
                  <div className="text-sm text-slate-500 mt-1">{selected.source}</div>
                  <div className="mt-5 space-y-2 text-sm">
                    <Row k="Email" v={selected.email} />
                    <Row k="Telefoon" v={selected.phone || "—"} />
                    <Row k="Postcode" v={selected.postcode || "—"} />
                    <Row k="Provincie" v={selected.province || "—"} />
                  </div>
                  {selected.estimated_savings !== null && selected.estimated_savings !== undefined && (
                    <div className="mt-5 p-4 bg-brand-soft/40 rounded-xl text-sm">
                      <div className="text-brand-deep font-bold mb-1">Calculator data</div>
                      <div>Panelen: {selected.panels} · Verbruik: {selected.annual_usage_kwh} kWh</div>
                      <div>Batterij: {selected.battery_capacity_kwh} kWh</div>
                      <div className="mt-1 font-semibold">Geschatte besparing: € {Number(selected.estimated_savings || 0).toLocaleString("nl-NL")}/jr</div>
                    </div>
                  )}
                  {selected.message && (
                    <div className="mt-5">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-1">Bericht</div>
                      <p className="text-sm text-slate-700">{selected.message}</p>
                    </div>
                  )}
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["new", "contacted", "won", "lost"].map((s) => (
                      <button
                        key={s}
                        onClick={async () => {
                          try {
                            await api.patch(`/admin/leads/${selected.id}?status=${s}`);
                            setSelected({ ...selected, status: s });
                            setLeads(leads.map((x) => x.id === selected.id ? { ...x, status: s } : x));
                            toast.success(`Status: ${s}`);
                          } catch { toast.error("Update mislukt"); }
                        }}
                        className={`text-xs px-3 py-1.5 rounded-full font-semibold ${selected.status === s ? "bg-brand text-white" : "bg-slate-100 text-navy-800 hover:bg-slate-200"}`}
                        data-testid={`status-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-slate-500 text-sm text-center py-12">Selecteer een lead om details te zien</div>
              )}
            </aside>
          </div>
        )}
      </div>
    </main>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-navy-900 truncate ml-3">{v}</span>
  </div>
);

export default AdminDashboard;
